import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import {
  PeopleFilterChipOption,
  PeopleFilterChipSelect
} from 'Src/views/GenericView/AdminView/Users/PeopleFilterChipSelect';
import { Sport } from '../../../../../models/sport';
import { getAllSchoolActivities, getSchoolSport } from '../../../../../helpers/service/admin/sports';
import { UnsavedChangesModal } from '../../../../../components/UnsavedChangesModal/UnsavedChangesModal';
import './Sports.css';

const PAGE_SIZE = 20;
const COLUMN_VISIBILITY_KEY = 'adminConsole.activitiesSports';

type Segment = 'all' | 'fav';
type SportColumnKey = 'activity' | 'format' | 'details' | 'updated' | 'favourite';
type FilterKey = 'formats' | 'capabilities';

interface SportsFilters {
  formats: string[];
  capabilities: string[];
}

const EMPTY_FILTERS: SportsFilters = {
  formats: [],
  capabilities: []
};

const TABLE_COLUMNS: Array<{ key: SportColumnKey; label: string; locked?: boolean }> = [
  { key: 'activity', label: 'Activity / Sport', locked: true },
  { key: 'format', label: 'Format' },
  { key: 'details', label: 'Details' },
  { key: 'updated', label: 'Updated' },
  { key: 'favourite', label: 'Favourite', locked: true }
];

const FORMAT_LABELS: Record<string, string> = {
  TEAM: 'Team',
  INDIVIDUAL: 'Individual',
  '1X1': '1 vs 1',
  '2X2': '2 vs 2'
};

interface State {
  items: Sport[];
  isLoading: boolean;
  isSaving: boolean;
  loadError: string | null;
  search: string;
  searchDraft: string;
  segment: Segment;
  selectedIds: string[];
  filters: SportsFilters;
  draftFilters: SportsFilters;
  isFiltersOpen: boolean;
  hiddenColumns: string[];
  isColumnsOpen: boolean;
  currentPage: number;
  toast: string | null;
  isLeaveWarningOpen: boolean;
  pendingLeaveLocation: any;
  isBypassingNavigationBlock: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Sports extends React.Component<Props, State> {
  toastTimer: any;
  columnsRef = React.createRef<HTMLDivElement>();
  private unblockNavigation?: () => void;

  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      isLoading: true,
      isSaving: false,
      loadError: null,
      search: '',
      searchDraft: '',
      segment: 'all',
      selectedIds: [],
      filters: { ...EMPTY_FILTERS },
      draftFilters: { ...EMPTY_FILTERS },
      isFiltersOpen: false,
      hiddenColumns: loadHiddenColumns(props.user, COLUMN_VISIBILITY_KEY).filter(field =>
        TABLE_COLUMNS.some(column => column.key === field && !column.locked)
      ),
      isColumnsOpen: false,
      currentPage: 1,
      toast: null,
      isLeaveWarningOpen: false,
      pendingLeaveLocation: null,
      isBypassingNavigationBlock: false
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
    this.setItems();
    this.unblockNavigation = this.props.history.block(nextLocation => {
      if (this.state.isBypassingNavigationBlock || !this.state.isSaving) return;

      this.setState({ isLeaveWarningOpen: true, pendingLeaveLocation: nextLocation });
      return false;
    });
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  componentWillUnmount() {
    clearTimeout(this.toastTimer);
    if (this.unblockNavigation) this.unblockNavigation();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onBeforeUnload = (event: BeforeUnloadEvent): string | void => {
    if (!this.state.isSaving) return;
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  keepEditing = () => {
    this.setState({ isLeaveWarningOpen: false, pendingLeaveLocation: null });
  };

  leaveWhileSaving = () => {
    const pendingLeaveLocation = this.state.pendingLeaveLocation;
    if (!pendingLeaveLocation) return this.keepEditing();

    this.setState({ isBypassingNavigationBlock: true, isLeaveWarningOpen: false }, () => {
      if (this.unblockNavigation) this.unblockNavigation();
      this.props.history.push(pendingLeaveLocation);
    });
  };

  onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (this.state.isColumnsOpen && this.columnsRef.current && !this.columnsRef.current.contains(target)) {
      this.setState({ isColumnsOpen: false });
    }
  };

  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    this.setState({ isFiltersOpen: false, isColumnsOpen: false });
  };

  setItems = () => {
    this.setState({ isLoading: true, loadError: null });
    getAllSchoolActivities(this.props.user)
      .then(items => {
        this.setState({ items, isLoading: false, loadError: null, currentPage: 1 });
      })
      .catch(error => {
        const status = error && error.response && error.response.status;
        this.setState({
          isLoading: false,
          loadError: status
            ? `Activities and sports could not be loaded (server error ${status}).`
            : 'Activities and sports could not be loaded. Check your connection and try again.'
        });
      });
  };

  icon(path: string, className?: string, filled?: boolean) {
    return (
      <svg
        className={className || 'activitiesSportsIcon'}
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={path} />
      </svg>
    );
  }

  starIcon(filled: boolean) {
    return this.icon(
      'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z',
      'activitiesSportsIcon',
      filled
    );
  }

  showToast = (toast: string) => {
    clearTimeout(this.toastTimer);
    this.setState({ toast });
    this.toastTimer = setTimeout(() => this.setState({ toast: null }), 2600);
  };

  getFilteredItems(): Sport[] {
    const search = this.state.search.trim().toLowerCase();
    const { filters, segment } = this.state;

    return this.state.items.filter(item => {
      if (segment === 'fav' && !item.isFavorite) return false;

      if (search) {
        const searchable = `${item.name || ''} ${item.description || ''}`.toLowerCase();
        if (!searchable.includes(search)) return false;
      }

      if (filters.formats.length > 0 && !filters.formats.includes(item.players)) return false;

      if (filters.capabilities.length > 0) {
        const matchesCapability = filters.capabilities.some(capability => {
          if (capability === 'multiparty') return Boolean(item.multiparty);
          if (capability === 'individualResults') return Boolean(item.individualResultsAvailable);
          return false;
        });
        if (!matchesCapability) return false;
      }

      return true;
    });
  }

  getVisibleItems(): Sport[] {
    return this.getFilteredItems()
      .slice()
      .sort(
        (left, right) =>
          Number(Boolean(right.isFavorite)) - Number(Boolean(left.isFavorite)) ||
          (left.name || '').localeCompare(right.name || '')
      );
  }

  getPageItems(): Sport[] {
    const start = (this.state.currentPage - 1) * PAGE_SIZE;
    return this.getVisibleItems().slice(start, start + PAGE_SIZE);
  }

  getSelectedItems(): Sport[] {
    return this.state.items.filter(item => this.state.selectedIds.includes(item.id));
  }

  getFavouriteCount(): number {
    return this.state.items.filter(item => item.isFavorite).length;
  }

  getActiveFilterCount(): number {
    return this.state.filters.formats.length + this.state.filters.capabilities.length;
  }

  getFormatLabel(sport: Sport): string {
    return FORMAT_LABELS[sport.players] || 'Not specified';
  }

  formatUpdatedAt(value?: string): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  setSegment = (segment: Segment) => {
    this.setState({ segment, selectedIds: [], currentPage: 1 });
  };

  setSearch = (search: string) => {
    this.setState({ search, searchDraft: search, selectedIds: [], currentPage: 1 });
  };

  applySearch = () => {
    this.setState(state => ({ search: state.searchDraft.trim(), selectedIds: [], currentPage: 1 }));
  };

  setCurrentPage = (currentPage: number) => {
    const pageCount = Math.max(1, Math.ceil(this.getVisibleItems().length / PAGE_SIZE));
    this.setState({ currentPage: Math.max(1, Math.min(currentPage, pageCount)), selectedIds: [] });
  };

  toggleSelection = (id: string, checked: boolean) => {
    this.setState(state => ({
      selectedIds: checked
        ? Array.from(new Set([...state.selectedIds, id]))
        : state.selectedIds.filter(value => value !== id)
    }));
  };

  togglePageSelection = (checked: boolean) => {
    const pageIds = this.getPageItems().map(item => item.id);
    this.setState(state => ({
      selectedIds: checked
        ? Array.from(new Set([...state.selectedIds, ...pageIds]))
        : state.selectedIds.filter(id => !pageIds.includes(id))
    }));
  };

  updateFavourite = (ids: string[], isFavorite: boolean) => {
    if (ids.length === 0 || this.state.isSaving) return;

    this.setState({ isSaving: true });
    const promises = ids.map(id => getSchoolSport(this.props.user, id, { isFavorite }));

    BPromise.all(promises)
      .then((updatedItems: Sport[]) => {
        this.setState(state => {
          const byId = updatedItems.reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {} as Record<string, Sport>);
          return {
            items: state.items.map(item => byId[item.id] || item),
            selectedIds: [],
            isSaving: false
          } as Pick<State, 'items' | 'selectedIds' | 'isSaving'>;
        });
        this.showToast(
          `${ids.length} ${ids.length === 1 ? 'activity' : 'activities'} ${
            isFavorite ? 'added to' : 'removed from'
          } favourites`
        );
      })
      .catch(() => {
        this.setState({ isSaving: false });
        this.showToast('Favourites could not be updated. Please try again.');
      });
  };

  toggleFavourite = (sport: Sport) => {
    this.updateFavourite([sport.id], !sport.isFavorite);
  };

  openFilters = () => {
    this.setState(state => ({
      isFiltersOpen: true,
      isColumnsOpen: false,
      draftFilters: {
        formats: [...state.filters.formats],
        capabilities: [...state.filters.capabilities]
      }
    }));
  };

  closeFilters = () => this.setState({ isFiltersOpen: false });

  toggleDraftFilter = (field: FilterKey, value: string) => {
    this.setState(state => {
      const current = state.draftFilters[field];
      return {
        draftFilters: {
          ...state.draftFilters,
          [field]: current.includes(value) ? current.filter(item => item !== value) : [...current, value]
        }
      } as Pick<State, 'draftFilters'>;
    });
  };

  applyFilters = () => {
    this.setState(state => ({
      filters: {
        formats: [...state.draftFilters.formats],
        capabilities: [...state.draftFilters.capabilities]
      },
      isFiltersOpen: false,
      selectedIds: [],
      currentPage: 1
    }));
  };

  clearFilters = () => {
    this.setState({
      filters: { ...EMPTY_FILTERS },
      draftFilters: { ...EMPTY_FILTERS },
      isFiltersOpen: false,
      selectedIds: [],
      currentPage: 1
    });
  };

  clearAllCriteria = () => {
    this.setState({
      search: '',
      searchDraft: '',
      filters: { ...EMPTY_FILTERS },
      draftFilters: { ...EMPTY_FILTERS },
      isFiltersOpen: false,
      selectedIds: [],
      currentPage: 1
    });
  };

  removeFilter = (field: FilterKey) => {
    this.setState(state => ({
      filters: { ...state.filters, [field]: [] },
      draftFilters: { ...state.draftFilters, [field]: [] },
      selectedIds: [],
      currentPage: 1
    }));
  };

  setHiddenColumns = (hiddenColumns: string[]) => {
    const allowed = hiddenColumns.filter(field => TABLE_COLUMNS.some(column => column.key === field && !column.locked));
    saveHiddenColumns(this.props.user, COLUMN_VISIBILITY_KEY, allowed);
    this.setState({ hiddenColumns: allowed });
  };

  toggleColumn = (column: SportColumnKey) => {
    const hidden = this.state.hiddenColumns.includes(column)
      ? this.state.hiddenColumns.filter(value => value !== column)
      : [...this.state.hiddenColumns, column];
    this.setHiddenColumns(hidden);
  };

  renderActivityTile(sport: Sport) {
    if (sport.icon) {
      return (
        <span className="activitiesSportsTile activitiesSportsTile--image">
          <img src={sport.icon} alt="" />
        </span>
      );
    }

    return (
      <span className="activitiesSportsTile">{this.icon('M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4')}</span>
    );
  }

  renderPageHeader() {
    const favouriteCount = this.getFavouriteCount();

    return (
      <header className="activitiesSportsPageHeader">
        <div className="activitiesSportsPageHeader__title">
          <h1>Activities & Sports</h1>
          <span>{this.state.items.length}</span>
        </div>
        <nav className="activitiesSportsTabs" aria-label="Activities and sports views">
          <button
            type="button"
            className={this.state.segment === 'all' ? 'is-active' : ''}
            onClick={() => this.setSegment('all')}
          >
            All activities
          </button>
          <button
            type="button"
            className={this.state.segment === 'fav' ? 'is-active' : ''}
            onClick={() => this.setSegment('fav')}
          >
            Favourites <span>{favouriteCount}</span>
          </button>
        </nav>
      </header>
    );
  }

  renderToolbar() {
    const activeFilterCount = this.getActiveFilterCount();
    const hidden = new Set(this.state.hiddenColumns);
    const visibleColumnCount = TABLE_COLUMNS.filter(column => column.locked || !hidden.has(column.key)).length;

    return (
      <div className="activitiesSportsListToolbar">
        <label className="activitiesSportsSearch">
          {this.icon('M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3')}
          <input
            value={this.state.searchDraft}
            onChange={event => this.setState({ searchDraft: event.target.value })}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                this.applySearch();
              }
            }}
            placeholder="Search by activity name or description"
          />
          {this.state.searchDraft ? (
            <button type="button" onClick={() => this.setSearch('')} aria-label="Clear search">
              ×
            </button>
          ) : null}
        </label>

        <button
          type="button"
          className={`activitiesSportsButton activitiesSportsButton--secondary${
            this.state.isFiltersOpen ? ' is-active' : ''
          }`}
          onClick={this.openFilters}
        >
          {this.icon('M4 6h16M7 12h10M10 18h4')}
          Filters
          {activeFilterCount > 0 ? <span className="activitiesSportsButton__badge">{activeFilterCount}</span> : null}
        </button>

        <div className="activitiesSportsColumns" ref={this.columnsRef}>
          <button
            type="button"
            className={`activitiesSportsButton activitiesSportsButton--secondary${
              this.state.isColumnsOpen ? ' is-active' : ''
            }`}
            onClick={() => this.setState({ isColumnsOpen: !this.state.isColumnsOpen, isFiltersOpen: false })}
          >
            {this.icon('M4 5h16v14H4zM10 5v14M16 5v14')}
            Columns <span className="activitiesSportsButton__badge">{visibleColumnCount}</span>
          </button>
          {this.state.isColumnsOpen ? (
            <div className="activitiesSportsColumnsMenu">
              <header>
                <strong>Show / hide columns</strong>
                <button type="button" onClick={() => this.setHiddenColumns([])}>
                  Reset
                </button>
              </header>
              {TABLE_COLUMNS.map(column => (
                <label key={column.key} className={column.locked ? 'is-locked' : ''}>
                  <input
                    type="checkbox"
                    checked={column.locked || !hidden.has(column.key)}
                    disabled={column.locked}
                    onChange={() => this.toggleColumn(column.key)}
                  />
                  <span>{column.label}</span>
                  {column.locked ? <small>Required</small> : null}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderFilters() {
    if (!this.state.isFiltersOpen) return null;

    const formatOptions: PeopleFilterChipOption[] = Object.keys(FORMAT_LABELS).map((value, order) => ({
      value,
      label: FORMAT_LABELS[value],
      order
    }));
    const capabilityOptions: PeopleFilterChipOption[] = [
      { value: 'individualResults', label: 'Individual results', order: 0 },
      { value: 'multiparty', label: 'Multiparty', order: 1 }
    ];

    return (
      <div className="activitiesSportsFilterOverlay" onMouseDown={this.closeFilters}>
        <aside
          className="activitiesSportsFilterDrawer"
          aria-label="Activities and sports filters"
          onMouseDown={event => event.stopPropagation()}
          onKeyDown={event => {
            if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'BUTTON') {
              event.preventDefault();
              this.applyFilters();
            }
          }}
        >
          <header>
            <h2>Filters</h2>
            <button type="button" onClick={this.closeFilters} aria-label="Close filters">
              ×
            </button>
          </header>
          <div className="activitiesSportsFilterDrawer__body">
            <PeopleFilterChipSelect
              label="Format"
              options={formatOptions}
              selected={this.state.draftFilters.formats}
              onToggle={value => this.toggleDraftFilter('formats', value)}
            />
            <PeopleFilterChipSelect
              label="Capabilities"
              options={capabilityOptions}
              selected={this.state.draftFilters.capabilities}
              onToggle={value => this.toggleDraftFilter('capabilities', value)}
            />
          </div>
          <footer>
            <button
              type="button"
              className="activitiesSportsButton activitiesSportsButton--ghost"
              onClick={this.clearFilters}
            >
              Clear all
            </button>
            <div>
              <button
                type="button"
                className="activitiesSportsButton activitiesSportsButton--secondary"
                onClick={this.closeFilters}
              >
                Cancel
              </button>
              <button
                type="button"
                className="activitiesSportsButton activitiesSportsButton--primary"
                onClick={this.applyFilters}
              >
                Apply
              </button>
            </div>
          </footer>
        </aside>
      </div>
    );
  }

  getFilterLabel(field: FilterKey): string {
    const labels =
      field === 'formats' ? FORMAT_LABELS : { individualResults: 'Individual results', multiparty: 'Multiparty' };
    return this.state.filters[field].map(value => labels[value] || value).join(', ');
  }

  renderActiveFilters() {
    const fields: Array<{ key: FilterKey; label: string }> = [
      { key: 'formats', label: 'Format' },
      { key: 'capabilities', label: 'Capabilities' }
    ];
    const active = fields.filter(field => this.state.filters[field.key].length > 0);
    const search = this.state.search.trim();
    if (active.length === 0 && !search) return null;

    return (
      <div className="activitiesSportsActiveFilters">
        {search ? (
          <span>
            <b>Search:</b> {search}
            <button type="button" onClick={() => this.setSearch('')} aria-label="Remove search filter">
              ×
            </button>
          </span>
        ) : null}
        {active.map(field => (
          <span key={field.key}>
            <b>{field.label}:</b> {this.getFilterLabel(field.key)}
            <button
              type="button"
              onClick={() => this.removeFilter(field.key)}
              aria-label={`Remove ${field.label} filter`}
            >
              ×
            </button>
          </span>
        ))}
        <button type="button" onClick={this.clearAllCriteria}>
          Clear all
        </button>
      </div>
    );
  }

  renderSelectionBar() {
    const selectedItems = this.getSelectedItems();
    if (selectedItems.length === 0) return null;

    const hasNotFavourite = selectedItems.some(item => !item.isFavorite);
    const hasFavourite = selectedItems.some(item => item.isFavorite);

    return (
      <div className="activitiesSportsSelectionBar">
        <div>
          <b>{selectedItems.length}</b> selected
        </div>
        <div className="activitiesSportsSelectionBar__actions">
          <button
            type="button"
            className="activitiesSportsButton activitiesSportsButton--ghost"
            onClick={() => this.setState({ selectedIds: [] })}
          >
            Clear selection
          </button>
          {hasNotFavourite ? (
            <button
              type="button"
              className="activitiesSportsButton activitiesSportsButton--primary"
              disabled={this.state.isSaving}
              onClick={() => this.updateFavourite(this.state.selectedIds, true)}
            >
              {this.starIcon(true)}
              Add to favourites
            </button>
          ) : null}
          {hasFavourite ? (
            <button
              type="button"
              className="activitiesSportsButton activitiesSportsButton--secondary"
              disabled={this.state.isSaving}
              onClick={() => this.updateFavourite(this.state.selectedIds, false)}
            >
              {this.starIcon(false)}
              Remove from favourites
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  renderTable() {
    const items = this.getPageItems();
    const selected = new Set(this.state.selectedIds);
    const hidden = new Set(this.state.hiddenColumns);
    const allPageSelected = items.length > 0 && items.every(item => selected.has(item.id));
    const visibleColumns = TABLE_COLUMNS.filter(column => column.locked || !hidden.has(column.key)).length;

    return (
      <div className="activitiesSportsTableScroll">
        <table className="activitiesSportsTable">
          <thead>
            <tr>
              <th className="activitiesSportsSelectCell">
                <SelectAllCheckbox
                  compact
                  isChecked={allPageSelected}
                  totalCount={this.getVisibleItems().length}
                  currentPage={this.state.currentPage}
                  pageItemsCount={items.length}
                  selectedItems={this.getSelectedItems()}
                  onSelectAllOnPageClick={() => this.togglePageSelection(true)}
                  onSelectAllClick={() => this.setState({ selectedIds: this.getVisibleItems().map(item => item.id) })}
                  onUnselectAllClick={() => this.setState({ selectedIds: [] })}
                />
              </th>
              <th>Activity / Sport</th>
              {!hidden.has('format') ? <th>Format</th> : null}
              {!hidden.has('details') ? <th>Details</th> : null}
              {!hidden.has('updated') ? <th>Updated</th> : null}
              <th className="activitiesSportsFavouriteHead">Favourite</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} className={selected.has(item.id) ? 'is-selected' : ''}>
                <td className="activitiesSportsSelectCell">
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={event => this.toggleSelection(item.id, event.target.checked)}
                    aria-label={`Select ${item.name}`}
                  />
                </td>
                <td>
                  <div className="activitiesSportsNameCell">
                    {this.renderActivityTile(item)}
                    <div>
                      <strong>{item.name}</strong>
                      <small>{item.description || 'No description added yet.'}</small>
                    </div>
                  </div>
                </td>
                {!hidden.has('format') ? (
                  <td>
                    <span className="activitiesSportsFormat">{this.getFormatLabel(item)}</span>
                  </td>
                ) : null}
                {!hidden.has('details') ? (
                  <td>
                    <div className="activitiesSportsDetails">
                      {item.individualResultsAvailable ? <span>Individual results</span> : null}
                      {item.multiparty ? <span>Multiparty</span> : null}
                      {!item.individualResultsAvailable && !item.multiparty ? <em>Standard</em> : null}
                    </div>
                  </td>
                ) : null}
                {!hidden.has('updated') ? (
                  <td className="activitiesSportsUpdated">{this.formatUpdatedAt(item.updatedAt)}</td>
                ) : null}
                <td className="activitiesSportsFavouriteCell">
                  <button
                    type="button"
                    className={item.isFavorite ? 'activitiesSportsStar is-active' : 'activitiesSportsStar'}
                    title={item.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
                    aria-label={
                      item.isFavorite ? `Remove ${item.name} from favourites` : `Add ${item.name} to favourites`
                    }
                    disabled={this.state.isSaving}
                    onClick={() => this.toggleFavourite(item)}
                  >
                    {this.starIcon(Boolean(item.isFavorite))}
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns + 1} className="activitiesSportsTable__empty">
                  <div>
                    {this.state.segment === 'fav'
                      ? this.starIcon(false)
                      : this.icon('M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3')}
                  </div>
                  <strong>
                    {this.state.segment === 'fav' ? 'No favourite activities match' : 'No activities match'}
                  </strong>
                  <span>Try changing the search or active filters.</span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    );
  }

  renderPagination() {
    const total = this.getVisibleItems().length;
    if (total === 0) return null;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const pageWindowStart = Math.max(1, Math.min(this.state.currentPage - 2, pageCount - 4));
    const pageWindowEnd = Math.min(pageCount, pageWindowStart + 4);
    const pages = Array.from({ length: pageWindowEnd - pageWindowStart + 1 }, (_, index) => pageWindowStart + index);

    return (
      <nav className="activitiesSportsPagination" aria-label="Activities and sports pages">
        <span>
          Page {this.state.currentPage} of {pageCount}
        </span>
        <button
          type="button"
          disabled={this.state.currentPage <= 1}
          onClick={() => this.setCurrentPage(this.state.currentPage - 1)}
        >
          Previous
        </button>
        {pages.map(page => (
          <button
            type="button"
            key={page}
            className={page === this.state.currentPage ? 'is-active' : ''}
            onClick={() => this.setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          disabled={this.state.currentPage >= pageCount}
          onClick={() => this.setCurrentPage(this.state.currentPage + 1)}
        >
          Next
        </button>
      </nav>
    );
  }

  renderToast() {
    if (!this.state.toast) return null;
    const isError = this.state.toast.indexOf('could not') !== -1;

    return (
      <div className={isError ? 'activitiesSportsToast activitiesSportsToast--error' : 'activitiesSportsToast'}>
        {isError ? this.icon('M12 8v5M12 17h.01') : this.icon('M20 6 9 17l-5-5')}
        {this.state.toast}
      </div>
    );
  }

  render() {
    if (this.state.isLoading) return <Loader />;

    if (this.state.loadError) {
      return (
        <div className="activitiesSportsPage">
          {this.renderPageHeader()}
          <div className="activitiesSportsShell">
            <div className="activitiesSportsLoadError" role="alert">
              <div>
                {this.icon(
                  'M12 9v4M12 17h.01M10.3 3.6 2.2 18a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0z'
                )}
              </div>
              <h2>Could not load activities and sports</h2>
              <p>{this.state.loadError}</p>
              <button
                type="button"
                className="activitiesSportsButton activitiesSportsButton--primary"
                onClick={this.setItems}
              >
                {this.icon('M20 11a8.1 8.1 0 1 1-2.4-5.7L20 7M20 2v5h-5')}
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="activitiesSportsPage">
        {this.renderPageHeader()}
        <div className="activitiesSportsShell">
          <section className="activitiesSportsListCard">
            {this.renderToolbar()}
            {this.renderActiveFilters()}
            {this.renderSelectionBar()}
            {this.renderTable()}
            {this.renderPagination()}
          </section>
        </div>
        {this.renderFilters()}
        {this.renderToast()}
        <UnsavedChangesModal
          isOpen={this.state.isLeaveWarningOpen}
          message="Favourite changes are still being saved. Leaving now may interrupt the update."
          onKeepEditing={this.keepEditing}
          onDiscard={this.leaveWhileSaving}
        />
      </div>
    );
  }
}
